import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  DEMO_START_GLUCOSE_MGDL,
  BEST_XP_KEY,
  displayGlucose,
} from '../config';
import { FOODS, FoodMonster, trueCarbs, scoutingOptions } from '../data/foods';
import {
  DEFAULT_GAME_BOLUS_SETTINGS,
  totalBolusUnits,
} from '../game/glucose/bolusCalculator';
import {
  simulateMeal,
  glucoseAt,
  minGlucose,
  trendArrow,
  GlucosePoint,
} from '../game/glucose/simulation';
import { PREDICTION_BANDS, scorePrediction } from '../game/glucose/prediction';

const FONT = 'Trebuchet MS, sans-serif';
const CHECK_IN_MIN = 120; // predict glucose 2h after the meal

type Phase = 'intro' | 'scout' | 'bolus' | 'timing' | 'predict' | 'battle' | 'results';

export const QUEST_SCENE_KEY = 'Quest';

export default class QuestScene extends Phaser.Scene {
  static readonly key = QUEST_SCENE_KEY;

  private phase: Phase = 'intro';
  private monster!: FoodMonster;
  private scoutAttempts = 0;
  private scoutCorrectFirstTry = false;
  private trustedCalculator = false;
  private preBolusMinutes = 0;
  private doseUnits = 0;
  private predictedMgdl = 0;
  private juicePacked = false;
  private curve: GlucosePoint[] = [];
  private revealIndex = 0;
  private chart!: Phaser.GameObjects.Graphics;

  private ui: Phaser.GameObjects.GameObject[] = [];
  private header!: Phaser.GameObjects.Text;
  private body!: Phaser.GameObjects.Text;
  private locked = false;

  constructor() {
    super(QUEST_SCENE_KEY);
  }

  create(): void {
    this.phase = 'intro';
    this.scoutAttempts = 0;
    this.scoutCorrectFirstTry = false;
    this.trustedCalculator = false;
    this.juicePacked = false;
    this.monster = Phaser.Utils.Array.GetRandom(FOODS);

    this.header = this.add
      .text(GAME_WIDTH / 2, 42, '', {
        fontFamily: FONT,
        fontSize: '30px',
        color: '#ffd166',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.body = this.add
      .text(GAME_WIDTH / 2, 92, '', {
        fontFamily: FONT,
        fontSize: '17px',
        color: '#dbe4ff',
        align: 'center',
        lineSpacing: 6,
      })
      .setWordWrapWidth(880) // wrap box so long lines stay centered on screen
      .setOrigin(0.5, 0);
    this.chart = this.add.graphics();
    this.ui = [];
    this.locked = false;

    this.startIntro();
  }

  // ---------- shared UI helpers ----------

  private clearUi(): void {
    this.ui.forEach((o) => o.destroy());
    this.ui = [];
    this.chart.clear();
  }

  private setHeader(t: string): void {
    this.header.setText(t);
  }

  private setBody(t: string): void {
    this.body.setText(t);
    this.body.setX(GAME_WIDTH / 2);
  }

  private makeOption(y: number, label: string, onClick: () => void): void {
    const w = 760;
    const h = 52;
    const x = GAME_WIDTH / 2 - w / 2;
    const bg = this.add.graphics().fillStyle(0x273052, 1).fillRoundedRect(x, y, w, h, 12);
    const txt = this.add
      .text(GAME_WIDTH / 2, y + h / 2, label, { fontFamily: FONT, fontSize: '19px', color: '#ffffff' })
      .setOrigin(0.5);
    // Zone origin is (0.5, 0.5): position it at the button CENTER so the
    // hit area matches the drawn rectangle exactly.
    const zone = this.add
      .zone(x + w / 2, y + h / 2, w, h)
      .setInteractive({ useHandCursor: true });
    const draw = (fill: number) => bg.clear().fillStyle(fill, 1).fillRoundedRect(x, y, w, h, 12);
    zone.on('pointerover', () => draw(0x3a4a7e));
    zone.on('pointerout', () => draw(0x273052));
    zone.on('pointerdown', () => {
      if (this.locked) return;
      draw(0x4cc9f0);
      onClick();
    });
    this.ui.push(bg, txt, zone);
  }

  // ---------- phase: intro ----------

  private startIntro(): void {
    this.phase = 'intro';
    this.clearUi();
    this.setHeader(`⚔️  WILD BOSS APPEARS: ${this.monster.name.toUpperCase()}`);
    this.setBody(
      `Tier ${this.monster.tier} meal-monster.\n${this.monster.taunt}\n\nDefeat it in 4 moves: SCOUT the label, CAST the Spellulator dose,\nCHARGE UP (timing), and PREDICT the curve at +2 hours.`,
    );
    this.makeOption(430, '🔍  SCOUT THE MONSTER (read the label)', () => this.startScout());
    this.makeOption(500, '🏃  Flee back to the Keep', () => this.scene.start('Menu'));
  }

  // ---------- phase: scout (carb counting) ----------

  private scoutOptions: number[] = [];

  private startScout(): void {
    this.phase = 'scout';
    this.scoutOptions = scoutingOptions(this.monster);
    this.renderScout();
  }

  private renderScout(): void {
    this.clearUi();
    this.setHeader('🔍  SCOUTING — read the label!');
    const remaining = this.scoutOptions.map((c) => `${c} g of carbs`).join('        ');
    this.setBody(`${this.monster.labelCard}\n\nTOTAL CARBS this monster delivers:  ${remaining}`);
    this.scoutOptions.forEach((carbs, i) => {
      this.makeOption(400 + i * 62, `${carbs} g`, () => this.tryScout(carbs));
    });
  }

  private tryScout(pick: number): void {
    this.scoutAttempts += 1;
    const truth = trueCarbs(this.monster);
    if (pick === truth) {
      if (this.scoutAttempts === 1) this.scoutCorrectFirstTry = true;
      this.startBolus();
      return;
    }
    // Wrong: monster mocks you, remove that option, retry. Never game-over.
    this.scoutOptions = this.scoutOptions.filter((c) => c !== pick);
    this.clearUi();
    this.setHeader('🔍  SCOUTING — the monster mocks you!');
    this.setBody(
      `"Ha! You miscounted my SERVINGS!"\n\nHint: servings × carbs per serving − fiber.\nTry again:`,
    );
    this.scoutOptions.forEach((carbs, i) => {
      this.makeOption(400 + i * 62, `${carbs} g`, () => this.tryScout(carbs));
    });
  }

  // ---------- phase: bolus (trust the calculator) ----------

  private startBolus(): void {
    this.phase = 'bolus';
    this.clearUi();
    const s = DEFAULT_GAME_BOLUS_SETTINGS;
    const correctDose = totalBolusUnits(trueCarbs(this.monster), DEMO_START_GLUCOSE_MGDL, s);
    this.setHeader('🪄  THE SPELLULATOR (in-game bolus calculator)');
    this.setBody(
      `Your power level: ${displayGlucose(DEMO_START_GLUCOSE_MGDL)}   |   Scouted carbs: ${trueCarbs(this.monster)} g\n` +
        `Spellulator says: ratio 1u : ${s.carbRatioGPerUnit} g   ·   1u drops 45 mg/dL   ·   target ${displayGlucose(s.targetGlucoseMgdl)}\n\n` +
        `THE SPELLULATOR PRESCRIBES: ${correctDose} units.\nWhat do you cast?`,
    );
    const options: { label: string; units: number; trusted: boolean }[] = [
      { label: `✅  Cast exactly ${correctDose} u — trust the Spellulator`, units: correctDose, trusted: true },
      { label: `🔥  DOUBLE CAST (${correctDose * 2} u) for SURE-NESS`, units: correctDose * 2, trusted: false },
      { label: `😤  Refuse to cast (0 u). You are one with the carb.`, units: 0, trusted: false },
    ];
    options.forEach((o, i) => {
      this.makeOption(400 + i * 62, o.label, () => {
        this.doseUnits = o.units;
        this.trustedCalculator = o.trusted;
        this.startTiming();
      });
    });
  }

  // ---------- phase: timing (pre-bolus combo) ----------

  private startTiming(): void {
    this.phase = 'timing';
    this.clearUi();
    this.setHeader('⏱️  CHARGE UP — when do you cast?');
    this.setBody(
      `Insulin magic takes time to wake up.\nCast EARLY and the spell is at full power when the carbs hit.`,
    );
    const options: { label: string; minutes: number }[] = [
      { label: '⚡ 15 min BEFORE the battle (CHARGE UP combo!)', minutes: 15 },
      { label: '🗡️ Right at the first strike', minutes: 0 },
      { label: '🦉 After the meal (fashionably late)', minutes: -30 },
    ];
    options.forEach((o, i) => {
      this.makeOption(380 + i * 62, o.label, () => {
        this.preBolusMinutes = o.minutes;
        this.startPredict();
      });
    });
  }

  // ---------- phase: predict (the core teaching loop) ----------

  private startPredict(): void {
    this.phase = 'predict';
    this.clearUi();
    this.setHeader(`🔮  DIVINATION — where is your power in 2 HOURS?`);
    this.setBody(
      `The battle is decided. Now READ YOUR OWN CURVE.\nPredict your glucose 2 hours after the meal:`,
    );
    PREDICTION_BANDS.forEach((b, i) => {
      this.makeOption(250 + i * 62, b.label, () => {
        this.predictedMgdl = b.value;
        this.showJuicePrompt();
      });
    });
  }

  private showJuicePrompt(): void {
    this.clearUi();
    this.setHeader('🧃  SUPPLY DROP');
    this.setBody(
      `Smart adventurers ALWAYS pack a fast-carb snack for a possible Low Ambush.\nPack a juice box (15 g fast carbs) in your bag?`,
    );
    this.makeOption(300, '🧃 Pack the juice box', () => {
      this.juicePacked = true;
      this.startBattle();
    });
    this.makeOption(370, "🎒 No room, it's heavy", () => {
      this.juicePacked = false;
      this.startBattle();
    });
  }

  // ---------- phase: battle (animated curve) ----------

  private startBattle(): void {
    this.phase = 'battle';
    this.curve = simulateMeal(
      {
        startGlucoseMgdl: DEMO_START_GLUCOSE_MGDL,
        carbsGrams: trueCarbs(this.monster), // the world uses the TRUE number
        fatLevel: this.monster.fatLevel,
        insulinUnits: this.doseUnits,
        preBolusMinutes: this.preBolusMinutes,
        activity: 'rest',
        mystery: 0.25,
        seed: this.monster.id.length * 991 + this.doseUnits * 7,
      },
      { hours: 4, stepMinutes: 5 },
    );
    this.revealIndex = 0;
    this.clearUi();
    this.setHeader('🌊  THE BATTLE RAGES...  (watch the curve!)');
    this.setBody('The line is your power level. Arrows show where it is HEADING.');
  }

  private chartX(t: number): number {
    return 80 + ((t + 30) / 270) * (GAME_WIDTH - 160);
  }

  private chartY(g: number): number {
    const clamped = Math.min(Math.max(g, 40), 400);
    return 520 - ((clamped - 40) / 360) * 380;
  }

  update(_time: number, delta: number): void {
    if (this.phase !== 'battle') return;
    this.revealIndex += delta / 55; // ~3s reveal
    const n = Math.min(Math.floor(this.revealIndex), this.curve.length);
    this.drawChart(n);
    if (n >= this.curve.length && !this.locked) {
      this.locked = true;
      this.time.delayedCall(900, () => this.showResults());
    }
  }

  private drawChart(upTo: number): void {
    const g = this.chart;
    g.clear();
    // subtle "normal" band (visual reference only — NEVER scored on)
    g.fillStyle(0x2a5d3f, 0.18).fillRect(80, this.chartY(180), GAME_WIDTH - 160, this.chartY(70) - this.chartY(180));
    // axes
    g.lineStyle(1, 0x3a4a7e, 1);
    g.strokeRect(80, 140, GAME_WIDTH - 160, 380);
    g.lineStyle(1, 0x5a6a99, 0.5);
    g.lineBetween(this.chartX(120), 140, this.chartX(120), 520); // +2h check-in line

    // curve
    g.lineStyle(3, 0x4cc9f0, 1);
    let last: GlucosePoint | null = null;
    for (let i = 0; i < upTo; i++) {
      const p = this.curve[i];
      if (last) g.lineBetween(this.chartX(last.tMinutes), this.chartY(last.glucoseMgdl), this.chartX(p.tMinutes), this.chartY(p.glucoseMgdl));
      last = p;
    }
    // prediction marker (only after prediction is on screen, near the end)
    if (upTo > this.curve.length * 0.75 && this.predictedMgdl > 0) {
      g.lineStyle(2, 0xffd166, 1);
      g.lineBetween(this.chartX(60), this.chartY(this.predictedMgdl), this.chartX(180), this.chartY(this.predictedMgdl));
    }
  }

  // ---------- phase: results (decision scoring, never glucose-number scoring) ----------

  private showResults(): void {
    this.phase = 'results';
    this.locked = false;
    this.clearUi();
    this.drawChart(this.curve.length);

    const scoutPts = this.scoutCorrectFirstTry ? 40 : this.scoutAttempts === 2 ? 20 : 0;
    const castPts = this.trustedCalculator ? 30 : 0;
    const timingPts = this.preBolusMinutes === 15 ? 30 : this.preBolusMinutes === 0 ? 15 : 5;
    const actual = glucoseAt(this.curve, CHECK_IN_MIN);
    const pred = scorePrediction(this.predictedMgdl, actual);
    const lowAmbush = minGlucose(this.curve) < 70;
    const preparedPts = lowAmbush && this.juicePacked ? 25 : 0;
    const totalXp = scoutPts + castPts + timingPts + pred.points + preparedPts;

    const best = Number(localStorage.getItem(BEST_XP_KEY) ?? 0);
    const isNewBest = totalXp > best;
    if (isNewBest) localStorage.setItem(BEST_XP_KEY, String(totalXp));

    // Replay hints: what happened and WHY — teaching, never blame.
    const hints: string[] = [];
    if (!this.scoutCorrectFirstTry) hints.push('• The monster had MORE carbs than you scouted — count servings, not pieces!');
    if (!this.trustedCalculator) hints.push('• Guessing doses beats nobody. The Spellulator exists so you never guess.');
    if (this.preBolusMinutes < 15 && pred.points < 60) hints.push('• Casting 15 min early lets insulin wake up BEFORE the carbs attack.');
    if (lowAmbush && this.juicePacked) hints.push('• LOW AMBUSH sprung — but your juice box was ready. Prepared wins. +25');
    if (lowAmbush && !this.juicePacked) hints.push('• A Low Ambush showed up! No shame — next run, pack the juice box. Always.');
    if (this.preBolusMinutes < 0) hints.push('• Casting late means chasing the spike. Charge up earlier next time.');
    if (hints.length === 0) hints.push('• Flawless reads. The curve had no secrets from you.');

    this.setHeader(`🏆  ${pred.rating.toUpperCase()}  ·  ${totalXp} XP${isNewBest ? '  ★ NEW BEST!' : ''}`);
    this.setBody(
      `Scouting ${scoutPts}/40 · Spellulator trust ${castPts}/30 · Timing ${timingPts}/30\n` +
        `Prediction ${pred.points}/100  (you called ${displayGlucose(this.predictedMgdl)}, curve says ${displayGlucose(actual)} at +2h)` +
        (preparedPts ? ` · Prepared ${preparedPts}/25` : '') +
        `\n+2h trend arrow: ${trendArrow(this.curve, CHECK_IN_MIN)}\n\n` +
        hints.join('\n'),
    );

    this.makeOption(470, '⚔️  Quest again (new monster)', () => {
      this.scene.restart();
    });
    this.makeOption(540, '🏰  Back to the Keep', () => this.scene.start('Menu'));
  }
}
